import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { CAT_META } from '../data/initialState';

const fmt = (n, lang) => {
  const num = Math.round(Math.abs(n || 0)).toLocaleString(lang==='en'?'en-US':lang==='sv'?'sv-SE':lang==='da'?'da-DK':'nb-NO');
  return lang==='en' ? '$' + num : num + (lang==='da'?' DKK':lang==='sv'?' SEK':' kr');
};

export default function TabUkerapport() {
  const { state } = useApp();
  const lang = state.language || 'en';
  const [weekOffset, setWeekOffset] = useState(0);

  // Get week range
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay() + 1 + (weekOffset * 7));
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);

  const weekLabel = `${startOfWeek.getDate()}/${startOfWeek.getMonth()+1} - ${endOfWeek.getDate()}/${endOfWeek.getMonth()+1}`;

  // Filter transactions for this week
  const weekTx = (state.transactions || []).filter(tx => {
    try {
      const d = new Date(tx.date);
      return d >= startOfWeek && d <= endOfWeek && !tx.locked;
    } catch(e) { return false; }
  });

  const totalSpent = weekTx.filter(t => t.amount < 0 && t.cat !== 'Sparing').reduce((s,t) => s + Math.abs(t.amount), 0);
  const totalSaved = weekTx.filter(t => t.cat === 'Sparing' || t.amount > 0).reduce((s,t) => s + Math.abs(t.amount), 0);
  const txCount = weekTx.length;
  const savingsRate = totalSpent + totalSaved > 0 ? Math.round((totalSaved / (totalSpent + totalSaved)) * 100) : 0;

  // By category
  const byCategory = {};
  weekTx.filter(t => t.amount < 0 && t.cat !== 'Sparing').forEach(tx => {
    const cat = tx.cat || 'Annet';
    if (!byCategory[cat]) byCategory[cat] = 0;
    byCategory[cat] += Math.abs(tx.amount);
  });
  const sortedCats = Object.entries(byCategory).sort((a,b) => b[1]-a[1]);
  const maxCatAmount = sortedCats[0]?.[1] || 1;

  // Daily spending this week
  const days = Array.from({length: 7}, (_, i) => {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    const dayTx = weekTx.filter(tx => {
      try { return new Date(tx.date).toDateString() === d.toDateString(); } catch(e) { return false; }
    });
    const spent = dayTx.filter(t => t.amount < 0).reduce((s,t) => s+Math.abs(t.amount), 0);
    const saved = dayTx.filter(t => t.cat === 'Sparing').reduce((s,t) => s+Math.abs(t.amount), 0);
    return { date: d, spent, saved };
  });
  const maxDay = Math.max(...days.map(d => d.spent + d.saved), 1);

  const dayNames = {
    en: ['Mo','Tu','We','Th','Fr','Sa','Su'],
    no: ['Ma','Ti','On','To','Fr','Lø','Sø'],
    sv: ['Må','Ti','On','To','Fr','Lö','Sö'],
    da: ['Ma','Ti','On','To','Fr','Lø','Sø'],
  };

  // Savvy tip
  const getSavvyTip = () => {
    if (totalSpent === 0) return {
      mood: 'excited',
      msg: lang==='en'?`No spending this week — incredible! 🌟`:lang==='sv'?`Inga utgifter denna vecka — otroligt! 🌟`:lang==='da'?`Ingen udgifter denne uge — utroligt! 🌟`:`Ingen utgifter denne uken — utrolig! 🌟`
    };
    if (savingsRate >= 30) return {
      mood: 'flex',
      msg: lang==='en'?`${savingsRate}% savings rate! You're crushing it! 💪`:lang==='sv'?`${savingsRate}% sparkvot! Du är grym! 💪`:lang==='da'?`${savingsRate}% opsparingsrate! Du er fantastisk! 💪`:`${savingsRate}% sparerate! Du er fantastisk! 💪`
    };
    if (savingsRate >= 10) return {
      mood: 'happy',
      msg: lang==='en'?`Good week! Try to save a little more next week 🎯`:lang==='sv'?`Bra vecka! Försök spara lite mer nästa vecka 🎯`:lang==='da'?`God uge! Prøv at spare lidt mere næste uge 🎯`:`Bra uke! Prøv å spare litt mer neste uke 🎯`
    };
    const bigCat = sortedCats[0];
    if (bigCat) return {
      mood: 'worried',
      msg: lang==='en'?`${bigCat[0]} was your biggest expense. Watch out next week! 👀`:lang==='sv'?`${bigCat[0]} var din största utgift. Var försiktig nästa vecka! 👀`:lang==='da'?`${bigCat[0]} var din største udgift. Vær forsigtig næste uge! 👀`:`${bigCat[0]} var din største utgift. Pass på neste uke! 👀`
    };
    return { mood: 'happy', msg: lang==='en'?'Keep going! Every kr saved counts! 🌱':lang==='sv'?'Fortsätt! Varje kr sparad räknas! 🌱':lang==='da'?'Bliv ved! Hver krone sparet tæller! 🌱':'Fortsett! Hver krone spart teller! 🌱' };
  };

  const tip = getSavvyTip();

  const isPro = state.plan === 'advanced' || state.plan === 'premium';

  if (!isPro) return (
    <div style={{ padding:'2rem', textAlign:'center' }}>
      <div className="savvy-float" style={{ marginBottom:20 }}>
        <img src="/mascot/savvy-excited.png" alt="Savvy" style={{ width:120, height:120, objectFit:'contain' }}/>
      </div>
      <div style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, color:'var(--ink)', marginBottom:8 }}>
        {lang==='en'?'Weekly Report':lang==='sv'?'Veckorapport':lang==='da'?'Ugerapport':'Ukentlig rapport'} 📊
      </div>
      <div style={{ fontSize:14, color:'var(--ink3)', marginBottom:24, lineHeight:1.6 }}>
        {lang==='en'?'Upgrade to Premium 👑 to see your weekly spending analysis, savings rate, and personalized Savvy tips!':lang==='sv'?'Uppgradera till Advanced för att se din veckovisa utgiftsanalys, sparkvot och personliga Savvy-tips!':lang==='da'?'Opgrader til Advanced for at se din ugentlige udgiftsanalyse, opsparingsrate og personlige Savvy-tips!':'Oppgrader til Premium 👑 for å se din ukentlige utgiftsanalyse, sparerate og personlige Savvy-tips!'}
      </div>
      <button onClick={() => {}} style={{ padding:'14px 32px', background:'linear-gradient(135deg,#378ADD,#7C3AED)', color:'#fff', border:'none', borderRadius:'var(--r-full)', fontSize:15, fontWeight:700, cursor:'pointer' }}>
        {lang==='en'?'Upgrade to Premium 👑 →':lang==='sv'?'Uppgradera till Premium 👑 →':lang==='da'?'Opgrader til Premium 👑 →':'Oppgrader til Premium 👑 →'}
      </button>
    </div>
  );

  return (
    <div style={{ padding:'1rem 1rem 2rem' }}>

      {/* Header + nav */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <button onClick={() => setWeekOffset(o => o-1)} style={{ padding:'6px 14px', borderRadius:'var(--r-md)', border:'1px solid var(--border)', background:'var(--surface)', cursor:'pointer', fontSize:14 }}>‹</button>
        <div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:700, color:'var(--ink)', textAlign:'center' }}>
            {lang==='en'?'Weekly Report':lang==='sv'?'Veckorapport':lang==='da'?'Ugerapport':'Ukerapport'}
          </div>
          <div style={{ fontSize:12, color:'var(--ink3)', textAlign:'center' }}>{weekLabel}</div>
        </div>
        <button onClick={() => setWeekOffset(o => Math.min(0, o+1))} disabled={weekOffset >= 0} style={{ padding:'6px 14px', borderRadius:'var(--r-md)', border:'1px solid var(--border)', background:'var(--surface)', cursor: weekOffset >= 0 ? 'default' : 'pointer', fontSize:14, opacity: weekOffset >= 0 ? 0.4 : 1 }}>›</button>
      </div>

      {/* Savvy tip */}
      <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:12, display:'flex', alignItems:'center', gap:12 }}>
        
        <div style={{ fontSize:13, color:'var(--ink)', lineHeight:1.5 }}>{tip.msg}</div>
      </div>

      {/* Stats grid */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
        {[
          [lang==='en'?'Total spent':lang==='sv'?'Totalt spenderat':lang==='da'?'Totalt brugt':'Totalt brukt', fmt(totalSpent,lang), 'var(--r400)'],
          [lang==='en'?'Total saved':lang==='sv'?'Totalt sparat':lang==='da'?'Totalt sparet':'Totalt spart', fmt(totalSaved,lang), '#22C97A'],
          [lang==='en'?'Transactions':lang==='sv'?'Transaktioner':lang==='da'?'Transaktioner':'Transaksjoner', txCount, 'var(--r400)'],
          [lang==='en'?'Savings rate':lang==='sv'?'Sparkvot':lang==='da'?'Opsparingsrate':'Sparerate', `${savingsRate}%`, 'var(--ink)'],
        ].map(([l,v,c]) => (
          <div key={l} style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:'14px 12px' }}>
            <div style={{ fontSize:11, color:'var(--ink3)', marginBottom:4 }}>{l}</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, color:c }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Daily bar chart */}
      <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:12 }}>
        <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:12 }}>
          {lang==='en'?'Daily overview':lang==='sv'?'Daglig översikt':lang==='da'?'Daglig oversigt':'Daglig oversikt'}
        </div>
        <div style={{ display:'flex', alignItems:'flex-end', gap:6, height:80 }}>
          {days.map((day, i) => {
            const hSpent = maxDay > 0 ? Math.round((day.spent/maxDay)*70) : 0;
            const hSaved = maxDay > 0 ? Math.round((day.saved/maxDay)*70) : 0;
            const isToday = day.date.toDateString() === new Date().toDateString();
            return (
              <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center' }}>
                <div style={{ width:'100%', display:'flex', flexDirection:'column', alignItems:'center', gap:1 }}>
                  {hSaved > 0 && <div style={{ width:'80%', height:hSaved, background:'#22C97A', borderRadius:'3px 3px 0 0' }}/>}
                  {hSpent > 0 && <div style={{ width:'80%', height:hSpent, background: isToday ? 'var(--g400)' : 'var(--r400)', borderRadius: hSaved > 0 ? 0 : '3px 3px 0 0' }}/>}
                  {hSpent === 0 && hSaved === 0 && <div style={{ width:'80%', height:4, background:'var(--border)', borderRadius:3 }}/>}
                </div>
                <div style={{ fontSize:9, color: isToday ? 'var(--g400)' : 'var(--ink3)', marginTop:4, fontWeight: isToday ? 700 : 400 }}>
                  {(dayNames[lang] || dayNames.en)[i]}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display:'flex', gap:12, marginTop:8 }}>
          <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'var(--ink3)' }}>
            <div style={{ width:8, height:8, background:'var(--r400)', borderRadius:2 }}/>
            {lang==='en'?'Spent':lang==='sv'?'Spenderat':lang==='da'?'Brugt':'Brukt'}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'var(--ink3)' }}>
            <div style={{ width:8, height:8, background:'#22C97A', borderRadius:2 }}/>
            {lang==='en'?'Saved':lang==='sv'?'Sparat':lang==='da'?'Sparet':'Spart'}
          </div>
        </div>
      </div>

      {/* Category breakdown */}
      {sortedCats.length > 0 && (
        <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:12 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:12 }}>
            {lang==='en'?'Spending by category':lang==='sv'?'Utgifter per kategori':lang==='da'?'Udgifter pr. kategori':'Utgifter per kategori'}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {sortedCats.slice(0,6).map(([cat, amount]) => {
              const meta = CAT_META[cat] || { icon:'💸', color:'#888' };
              const pct = Math.round((amount/maxCatAmount)*100);
              return (
                <div key={cat}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                    <div style={{ fontSize:12, color:'var(--ink)', display:'flex', alignItems:'center', gap:6 }}>
                      <span>{meta.icon}</span> {cat}
                    </div>
                    <div style={{ fontSize:12, fontWeight:700, color:'var(--ink2)' }}>{fmt(amount, lang)}</div>
                  </div>
                  <div style={{ background:'var(--surface)', borderRadius:99, height:6, overflow:'hidden' }}>
                    <div style={{ width:`${pct}%`, height:'100%', background: meta.color || 'var(--g400)', borderRadius:99, transition:'width 0.5s ease' }}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No data */}
      {txCount === 0 && (
        <div style={{ textAlign:'center', padding:'2rem', color:'var(--ink3)', fontSize:14 }}>
          📊 {lang==='en'?'No transactions this week yet! Start tracking your spending 💪':lang==='sv'?'Inga transaktioner denna vecka ännu! Börja spåra dina utgifter 💪':lang==='da'?'Ingen transaktioner denne uge endnu! Begynd at spore dine udgifter 💪':'Ingen transaksjoner denne uken ennå! Begynn å registrere forbruket ditt 💪'}
        </div>
      )}

      <div style={{ height:16 }}/>
    </div>
  );
}
